import React, { useState, useEffect } from 'react';
import { FiMoreVertical, FiEdit3, FiFileText, FiFolder, FiTrash2, FiDownload, FiUpload, FiAnchor } from 'react-icons/fi';
import { HiPlay } from 'react-icons/hi2';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Input from './ui/Input';
import AssetManager from '../services/AssetManager.ts';
import LiveBackupManager from '../services/LiveBackupManager.js';

const ScheduleOptionsMenu = ({
    currentYear,
    isLiveMode = true,
    currentDraftName = null,
    onModeChange,
    onDraftChange,
    onSaveCurrent,
    onPublishDraft,
    onFileUpload,
    onExport,
    isImporting = false,
    isExporting = false,
    importError,
    importSuccess,
    isLoading = false,
    error,
    success,
    setError,
    setSuccess,
    setIsLoading,
    draftManager = null, // NEW: Accept DraftManager instance
    onToggleDebug // NEW: Handler to toggle debug drawer
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [drafts, setDrafts] = useState([]);

    // Modal states
    const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedDraftToDelete, setSelectedDraftToDelete] = useState(null);
    const [newDraftName, setNewDraftName] = useState('');
    const [isNewDraftModalOpen, setIsNewDraftModalOpen] = useState(false);
    const [newEmptyDraftName, setNewEmptyDraftName] = useState('');

    // File input ref for import
    const fileInputRef = React.useRef(null);

    const assetManager = AssetManager.getInstance();

    // Fetch available drafts and listen to changes
    useEffect(() => {
        if (draftManager) {
            fetchDrafts();

            // Listen to draft manager changes (like renames)
            const handleDraftManagerChange = (state) => {
                if (state.drafts) {
                    // Exclude automatic backups from normal drafts list
                    const filtered = (state.drafts || []).filter(d => !(d?.draft_name && LiveBackupManager.isBackupName(String(d.draft_name))));
                    setDrafts(filtered);
                }
            };

            draftManager.addListener(handleDraftManagerChange);

            // Cleanup listener on unmount
            return () => {
                draftManager.removeListener(handleDraftManagerChange);
            };
        }
    }, [draftManager]);

    const fetchDrafts = async () => {
        try {
            if (draftManager) {
                const draftList = await draftManager.fetchDrafts();
                // Exclude automatic backups from normal drafts list
                const filtered = (draftList || []).filter(d => !(d?.draft_name && LiveBackupManager.isBackupName(String(d.draft_name))));
                setDrafts(filtered);
            }
            if (setError) setError(null);
        } catch (err) {
            console.error('Failed to load drafts:', err);
            if (setError) setError('Failed to load drafts: ' + err.message);
        }
    };

    const handleSaveAs = async () => {
        if (!newDraftName.trim()) {
            if (setError) setError('Please enter a draft name');
            return;
        }

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.createDraft(newDraftName.trim(), currentYear, false);
            if (setSuccess) setSuccess(`Draft "${newDraftName}" created successfully!`);
            onModeChange(false, newDraftName.trim());
            setIsSaveAsModalOpen(false);
            setNewDraftName('');
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
            await fetchDrafts();
        } catch (err) {
            if (setError) setError('Failed to create draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleCreateEmptyDraft = async () => {
        if (!newEmptyDraftName.trim()) {
            if (setError) setError('Please enter a draft name');
            return;
        }

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.createEmptyDraft(newEmptyDraftName.trim(), currentYear);
            if (setSuccess) setSuccess(`Empty draft "${newEmptyDraftName}" created successfully!`);
            onModeChange(false, newEmptyDraftName.trim());
            setIsNewDraftModalOpen(false);
            setNewEmptyDraftName('');
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
            await fetchDrafts();
        } catch (err) {
            if (setError) setError('Failed to create empty draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleLoadDraft = async (draftName) => {
        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draftName);
            }
            onModeChange(false, draftName);
            if (setSuccess) setSuccess(`Loaded draft "${draftName}"`);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            if (setError) setError('Failed to load draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
            setIsMenuOpen(false);
        }
    };

    const handleDeleteDraft = async () => {
        if (!selectedDraftToDelete) return;

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.deleteDraft(selectedDraftToDelete);
            if (setSuccess) setSuccess(`Draft "${selectedDraftToDelete}" deleted successfully!`);

            // If we're currently viewing the deleted draft, switch to live mode
            if (currentDraftName === selectedDraftToDelete) {
                onModeChange(true, null);
            }

            setIsDeleteModalOpen(false);
            setSelectedDraftToDelete(null);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
            await fetchDrafts();
        } catch (err) {
            if (setError) setError('Failed to delete draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handlePublish = async () => {
        if (!currentDraftName) {
            if (setError) setError('No draft selected to publish');
            return;
        }

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.publishDraft(currentDraftName, currentYear);
            onModeChange(true, null);
            if (setSuccess) setSuccess(`Draft "${currentDraftName}" published to live schedule!`);
            setIsMenuOpen(false);
            if (setSuccess) setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            if (setError) setError('Failed to publish draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleLoadLive = () => {
        if (draftManager) {
            draftManager.setLiveMode();
        }
        onModeChange(true, null);
        setIsMenuOpen(false);
        if (setSuccess) setSuccess('Switched to live schedule');
        if (setSuccess) setTimeout(() => setSuccess(null), 3000);
    };

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
        setIsMenuOpen(false);
    };

    const handleExportClick = () => {
        if (onExport) {
            onExport();
        }
        setIsMenuOpen(false);
    };

    const handleToggleDebug = () => {
        if (onToggleDebug) {
            onToggleDebug();
        }
        setIsMenuOpen(false);
    };

    return (
        <div className="relative">
            {/* Options Button */}
            <Button
                variant="outline"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                disabled={isLoading}
                className="flex items-center"
            >
                <FiMoreVertical className="w-5 h-5" />
            </Button>

            {/* Dropdown Menu */}
            {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-2">
                        {/* Current Mode Indicator */}
                        <div className="px-3 py-2 text-sm bg-gray-50 rounded mb-2">
                            <strong>Current Mode:</strong> {isLiveMode ? 'Live Schedule' : `Draft: ${currentDraftName}`}
                        </div>

                        {/* Draft Actions */}
                        <div className="border-b border-gray-100 pb-2 mb-2">
                            <div className="text-xs text-gray-500 uppercase tracking-wide px-3 py-1">Draft Actions</div>

                            {!isLiveMode && currentDraftName && (
                                <button
                                    onClick={onSaveCurrent}
                                    disabled={isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded"
                                >
                                    Save "{currentDraftName}"
                                </button>
                            )}

                            <button
                                onClick={() => setIsSaveAsModalOpen(true)}
                                disabled={isLoading}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2"
                            >
                                <FiEdit3 className="w-4 h-4" /> Save as...
                            </button>

                            <button
                                onClick={() => setIsNewDraftModalOpen(true)}
                                disabled={isLoading}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2"
                            >
                                <FiFileText className="w-4 h-4" /> New Empty Draft...
                            </button>

                            {/* Load Submenu */}
                            <div className="relative group">
                                <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <FiFolder className="w-4 h-4" /> Load
                                    </span>
                                    <HiPlay className="w-3 h-3" />
                                </button>

                                {/* Load Submenu */}
                                <div className="absolute right-full top-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg hidden group-hover:block">
                                    <div className="p-1">
                                        <button
                                            onClick={handleLoadLive}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2"
                                        >
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div> Live Schedule
                                        </button>
                                        {drafts.length > 0 && (
                                            <div className="border-t border-gray-100 my-1"></div>
                                        )}
                                        {drafts.map((draft, index) => (
                                            <div key={index} className="flex items-center justify-between group/draft">
                                                <button
                                                    onClick={() => handleLoadDraft(draft.draft_name)}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2"
                                                >
                                                    {draft.draft_name}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedDraftToDelete(draft.draft_name);
                                                        setIsDeleteModalOpen(true);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover/draft:opacity-100"
                                                    title={`Delete draft: ${draft.draft_name}`}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                        {drafts.length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-500">No drafts available</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {!isLiveMode && currentDraftName && (
                                <button
                                    onClick={() => {
                                        setSelectedDraftToDelete(currentDraftName);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    disabled={isLoading}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 rounded flex items-center gap-2"
                                >
                                    <FiTrash2 className="w-4 h-4" /> Delete "{currentDraftName}"
                                </button>
                            )}
                        </div>

                        {/* Publishing */}
                        {/* <div className="border-b border-gray-100 pb-2 mb-2">
                            <div className="text-xs text-gray-500 uppercase tracking-wide px-3 py-1">Publishing</div>
                            <button
                                onClick={onPublishDraft}
                                disabled={isLoading || isLiveMode || !currentDraftName}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 text-green-600 rounded disabled:text-gray-400 disabled:hover:bg-gray-50"
                            >
                                Publish Draft
                            </button>
                        </div> */}

                        {/* Import/Export */}
                        <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide px-3 py-1">Import/Export</div>
                            <button
                                onClick={handleImportClick}
                                disabled={isLoading || isImporting}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2 disabled:text-gray-400"
                            >
                                <FiDownload className="w-4 h-4" /> {isImporting ? 'Importing...' : 'Import Schedule...'}
                            </button>

                            <button
                                onClick={handleExportClick}
                                disabled={isLoading || isExporting}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2 disabled:text-gray-400"
                            >
                                <FiUpload className="w-4 h-4" /> {isExporting ? 'Exporting...' : 'Export Schedule...'}
                            </button>
                        </div>

                        {/* Debug Toggle */}
                        {onToggleDebug && (
                            <div className="pt-2">
                                <button
                                    onClick={handleToggleDebug}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center gap-2"
                                >
                                    <FiAnchor className="w-4 h-4" /> Show Debug Info
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Click outside to close */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsMenuOpen(false)}
                />
            )}

            {/* Hidden file input for import */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv"
                onChange={onFileUpload}
                style={{ display: 'none' }}
            />

            {/* Error Messages - Success messages are handled by parent component */}
            {(error || importError) && (
                <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
                    <span className="block sm:inline">{error || importError}</span>
                    <button
                        className="float-right ml-4 text-red-500 hover:text-red-700"
                        onClick={() => {
                            if (setError) setError(null);
                            // Note: importError is managed by parent component
                        }}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Success messages are now handled by the parent ClassSchedulerPage component */}

            {/* Save As Modal */}
            <Modal
                isOpen={isSaveAsModalOpen}
                onClose={() => {
                    setIsSaveAsModalOpen(false);
                    setNewDraftName('');
                }}
                title="Save Schedule as Draft"
            >
                <div className="space-y-4">
                    <p className="text-gray-600">
                        Save the current schedule for year {currentYear} as a new draft.
                    </p>
                    <Input
                        label="Draft Name"
                        value={newDraftName}
                        onChange={(e) => setNewDraftName(e.target.value)}
                        placeholder="Enter draft name..."
                        required
                    />
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsSaveAsModalOpen(false);
                                setNewDraftName('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveAs}
                            disabled={isLoading || !newDraftName.trim()}
                        >
                            {isLoading ? 'Saving...' : 'Save Draft'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* New Empty Draft Modal */}
            <Modal
                isOpen={isNewDraftModalOpen}
                onClose={() => setIsNewDraftModalOpen(false)}
                title="Create New Empty Draft"
            >
                <div className="p-4">
                    <p className="text-sm text-gray-600 mb-4">
                        Enter a name for the new empty draft.
                    </p>
                    <Input
                        type="text"
                        value={newEmptyDraftName}
                        onChange={(e) => setNewEmptyDraftName(e.target.value)}
                        placeholder="e.g., 'Term 3 Scratchpad'"
                        className="w-full"
                    />
                    <div className="mt-6 flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setIsNewDraftModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateEmptyDraft}
                            disabled={isLoading || !newEmptyDraftName.trim()}
                        >
                            {isLoading ? 'Creating...' : 'Create Draft'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedDraftToDelete(null);
                }}
                title="Delete Draft"
            >
                <div className="space-y-4">
                    <p className="text-gray-600">
                        Are you sure you want to delete the draft "{selectedDraftToDelete}"? This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsDeleteModalOpen(false);
                                setSelectedDraftToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteDraft}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Deleting...' : 'Delete Draft'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ScheduleOptionsMenu; 